import { Injectable } from '@angular/core';
import { MOVEMENT_TRANSFER_IN, WEIGHT_DECIMALS } from '../models/product-stock-movement.model';

/**
 * A movement's two scale readings, as much of one as any weight figure needs.
 *
 * Structural on purpose: `ProductStockMovement`, `MovementRow` and `RecentMovement` all satisfy it
 * without declaring they do, so the screens that read the ledger through CRUD and the screens that
 * read it through the reporting endpoints get the same answers from the same code.
 */
export interface WeighedMovement {
  movementType: string;
  startWeight: number;
  endWeight: number;
}

/**
 * Everything the warehousing screens do with a weight. The browser-side counterpart of the
 * backend's `product-stock-weight.ts`, and deliberately NOT part of `StockQuantityService`.
 *
 * There is no epsilon here and there must never be one. `qty` needs a tolerance because it is a
 * MySQL float and a replayed level rests on its own storage error; a weight is DECIMAL(12,3) and is
 * exact, so it has no storage error to absorb. An epsilon on a weight would report a real 1 g
 * discrepancy as zero, which is the false-clean total this module has already had to write up once.
 * `round`, `isZero` and `isNegative` on the quantity service are the wrong precedent for this file.
 *
 * Nothing here enforces anything. A weight is recorded and checked against nothing: not the quantity
 * on its row, not a product weight, and not the other reading in its own pair. In particular an end
 * weight below the start is the correct reading on anything going out, so it is presented as it
 * stands and flagged nowhere.
 */
@Injectable({
  providedIn: 'root'
})
export class StockWeightService {

  /**
   * The movement types whose weights are a duplicate of another row's rather than a second weighing.
   *
   * A transfer is one physical weighing written onto two ledger rows: `transferMovements` puts the
   * line's single pair of readings on both the transfer-out and the transfer-in row, so the goods do
   * not appear to arrive unweighed at the destination. The consequence is that any total over a set
   * holding both sides counts that weighing twice, and it does not cancel by itself the way `qty`
   * does — the signs cancel a quantity, but the two rows carry identical readings and therefore
   * identical nets.
   *
   * So this list is read by every weight total on every screen, and per-row presentation ignores it:
   * both rows show their weights, which is correct, and only the aggregates leave one out. Two
   * definitions of which rows a weight total covers would be two answers to what the goods weighed.
   */
  private _duplicatedMovementTypes: string[] = [MOVEMENT_TRANSFER_IN];

  /**
   * Whether this row's weights may enter a total. Per-row display must NOT consult this.
   *
   * @param movementType the row's movement type
   */
  countsTowardTotal(movementType: string): boolean {
    return this._duplicatedMovementTypes.indexOf(movementType) === -1;
  }

  /**
   * A weight for display: blank where none was taken, and never an en dash or a zero.
   *
   * Zero renders as '0' and must: it is a real reading off a tared scale, and blanking it would say
   * the goods were never weighed. Only a null is blank. `Number()` is not used to test the value for
   * the same reason — `Number(null)` is 0, which is how an unweighed movement comes to be presented
   * as one that weighed nothing.
   *
   * @param weight a reading off a movement row, or null where it was not weighed
   */
  format(weight: number): string {
    if (weight === null || weight === undefined) {
      return '';
    }

    return String(this.trim(Number(weight)));
  }

  /**
   * Net moved, or null unless BOTH readings are present.
   *
   * The null propagation is the point rather than an accident: this matches the bare `endWeight -
   * startWeight` the reports select, where SQL makes `NULL - 5` NULL. A row weighed only once has no
   * net, and reporting 0 for it would say "weighed twice, nothing moved" — a different statement
   * about a different pallet. Never defaulted to zero, here or anywhere.
   *
   * @param startWeight reading before the movement
   * @param endWeight reading after it
   */
  net(startWeight: number, endWeight: number): number {
    if (startWeight === null || startWeight === undefined
      || endWeight === null || endWeight === undefined) {
      return null;
    }

    return this.trim(Number(endWeight) - Number(startWeight));
  }

  /** The net of one row, formatted. Blank where the row was weighed once or not at all. */
  formatNet(movement: WeighedMovement): string {
    return this.format(this.net(movement.startWeight, movement.endWeight));
  }

  /**
   * Net weight over a set of rows, or null where none of them was weighed twice.
   *
   * Null and not 0 when nothing is covered, mirroring SQL: `SUM()` over zero non-NULL rows is NULL,
   * and a screen showing 0.000 for a set nobody weighed is stating a measurement. Rows the predicate
   * excludes contribute nothing, so a transfer is counted once.
   *
   * NEVER render this without `unweighed()` beside it. `SUM` skips what it cannot add, so this is a
   * real number that says nothing about how much of the set it covers.
   *
   * @param movements the rows on screen
   */
  netTotal(movements: WeighedMovement[]): number {
    const counted: WeighedMovement[] = this.counted(movements);

    const nets: number[] = counted
      .map((movement: WeighedMovement) => this.net(movement.startWeight, movement.endWeight))
      .filter((net: number) => net !== null);

    if (nets.length === 0) {
      return null;
    }

    return this.trim(nets.reduce((total: number, net: number) => total + net, 0));
  }

  /**
   * How many rows the net total is actually built from — those weighed at both ends.
   *
   * @param movements the rows on screen
   */
  weighed(movements: WeighedMovement[]): number {
    return this.counted(movements)
      .filter((movement: WeighedMovement) => this.net(movement.startWeight, movement.endWeight) !== null)
      .length;
  }

  /**
   * How many rows the net total does NOT cover. Must be rendered including when it is zero.
   *
   * The count is what makes the total readable at all: 900 unweighed movements is a rounding error
   * in a million and the entire report in a thousand. A net total whose coverage is invisible is the
   * plausible-looking figure this module keeps having to write up.
   *
   * Counted over the same rows as the total, so the second row of a transfer is not held against
   * coverage — its weighing is already in the figure through the other side.
   *
   * @param movements the rows on screen
   */
  unweighed(movements: WeighedMovement[]): number {
    return this.counted(movements).length - this.weighed(movements);
  }

  private counted(movements: WeighedMovement[]): WeighedMovement[] {
    const rows: WeighedMovement[] = movements ? movements : [];

    return rows.filter((movement: WeighedMovement) => this.countsTowardTotal(movement.movementType));
  }

  /**
   * Cuts binary-float noise at the column's own scale. Display formatting, NOT a tolerance.
   *
   * These are JS numbers, so `1250.125 - 1000.05` reads back as 250.07499999999999 and a sum of a
   * day's tickets trails digits the DECIMAL(12,3) column cannot hold. Nothing real is lost at three
   * decimals because nothing finer is stored; no value is compared against anything here, and no
   * difference is treated as zero.
   */
  private trim(weight: number): number {
    if (!Number.isFinite(weight)) {
      return weight;
    }

    return Number(weight.toFixed(WEIGHT_DECIMALS));
  }
}
